import { Aide } from '@/components/ui/aide';
import React, { useMemo } from 'react';

import { StatsCompactes } from '../ui/stats-compactes';
import { Sparkline } from '../ui/sparkline';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { WORK_TYPE_LABELS } from '../../lib/maintenanceLabels';
import { referenceLisible } from '../../lib/libelles';
import { usePagination } from '../../hooks/usePagination';
import { BarrePagination } from '../ui/pagination-bar';
import { maintenanceDateLabel, maintenanceMoney } from './MaintenanceTimeline';

const num = (value) => Number(value || 0);

/** Dépense atelier cumulée du camion: total, par nature, dans le temps. */
export default function MaintenanceCosts({ data }) {
  const costSeries = useMemo(() => {
    if (!data) return [];
    const byDay = new Map();
    data.orders.forEach((order) => {
      const stamp = order.completed_at || order.created_at;
      if (!stamp || !num(order.total_cost)) return;
      const key = String(stamp).slice(0, 10);
      byDay.set(key, (byDay.get(key) || 0) + num(order.total_cost));
    });
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({ date, count: Math.round(count) }));
  }, [data]);

  const totals = useMemo(() => {
    const byType = {};
    let total = 0;
    (data?.orders || []).forEach((order) => {
      const cost = num(order.total_cost);
      total += cost;
      const key = order.work_type || 'corrective';
      byType[key] = (byType[key] || 0) + cost;
    });
    return { total, byType };
  }, [data]);

  // Les ordres de travail du camion se lisent par pages; la dépense, elle, porte sur tous.
  const pageOrdres = usePagination(data?.orders || [], { cle: 'ordres' });

  if (!data) return null;

  return (
    <div className="space-y-3">
      <StatsCompactes
        stats={[
          {
            label: 'Dépense totale',
            valeur: totals.total,
            unite: 'FCFA',
            aide: `${data.orders.length} intervention(s)`,
          },
          ...Object.entries(totals.byType)
            .slice(0, 3)
            .map(([type, amount]) => ({
              label: WORK_TYPE_LABELS[type],
              valeur: amount,
              unite: 'FCFA',
              aide: totals.total
                ? `${Math.round((amount / totals.total) * 100)} % de la dépense`
                : undefined,
            })),
        ]}
        plus={
          Object.keys(totals.byType).length > 3 ? (
            <ul className="space-y-1 text-sm">
              {Object.entries(totals.byType)
                .slice(3)
                .map(([type, amount]) => (
                  <li key={type} className="flex justify-between gap-3">
                    <span>{WORK_TYPE_LABELS[type]}</span>
                    <span className="tabular-nums">
                      {amount.toLocaleString('fr-FR')} FCFA
                    </span>
                  </li>
                ))}
            </ul>
          ) : undefined
        }
      />

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h3 className="text-base">Dépense engagée dans le temps</h3>
        {costSeries.length === 0 ? (
          <p className="flex items-center gap-1 text-sm text-muted-foreground">
            Aucune intervention chiffrée pour ce camion.
            <Aide titre="Courbe de dépense">
              La courbe apparaît dès qu’un ordre de travail est clôturé avec son coût de main d’œuvre et
              de pièces.
            </Aide>
          </p>
        ) : (
          <Sparkline points={costSeries} label="Dépense atelier (FCFA)" height={80} />
        )}
      </section>

      {data.orders.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <Table stickyFirstColumn>
            <TableHeader>
              <TableRow>
                <TableHead>Ordre</TableHead>
                <TableHead>Nature</TableHead>
                <TableHead>Terminé le</TableHead>
                <TableHead>Main d’œuvre</TableHead>
                <TableHead>Pièces</TableHead>
                <TableHead>Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageOrdres.lignes.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <p className="font-mono text-xs text-muted-foreground">{referenceLisible(order.order_number)}</p>
                    <p className="font-medium">{order.title}</p>
                  </TableCell>
                  <TableCell>{WORK_TYPE_LABELS[order.work_type]}</TableCell>
                  <TableCell>{maintenanceDateLabel(order.completed_at)}</TableCell>
                  <TableCell className="font-mono tabular">
                    {maintenanceMoney(order.labor_cost)}
                  </TableCell>
                  <TableCell className="font-mono tabular">
                    {maintenanceMoney(order.parts_cost)}
                  </TableCell>
                  <TableCell className="font-mono tabular font-semibold">
                    {maintenanceMoney(order.total_cost)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <BarrePagination pagination={pageOrdres} nom="ordres de travail" className="mt-0" />
    </div>
  );
}
